import { PostAttachmentType } from '__generated__/graphql/legacy/graphql';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import useFileOperations from 'hooks/useFileOperations';
import {
  Control,
  FieldErrors,
  FieldValue,
  useController,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ACCEPTED_FILE_TYPES } from 'utils/constants';
import { validateFiles } from 'utils/fileHelper';

import React, { useEffect, useState } from 'react';

import { Button, InlineNotification } from '@thinkific/chalk/react-components';
import {
  Image,
  Paperclip,
  Smiley,
  TextAa,
  VideoCamera,
} from '@thinkific/chalk/react-icons';

import { EditorContent } from '@tiptap/react';

import { AttachmentPreview } from 'components/AttachmentPreview/AttachmentPreview';
import AttachmentVideoInput from 'components/AttachmentVideoInput/AttachmentVideoInput';
import { useCommunityContext } from 'components/CommunityContext';
import { FormValues as PostFormValues } from 'components/PostForm/PostForm';
import { FormValues as ReplyEditorFormValues } from 'components/ReplyEditorContext/ReplyEditorContext';

import { Attachment } from 'schema/Post/types';

import { useGlobalState } from 'components/new/providers/GlobalStateProvider';
import { Content, useEditor } from '../hooks/useEditor/useEditor';
import ActionButton from './ActionButton';
import AttachmentInput from './Actions/AttachmentInput/AttachmentInput';
import EmojiPicker from './Actions/EmojiPicker/EmojiPicker';
import MenuBar from './Actions/MenuBar';
import * as css from './DocumentEditor.css';

type AllFormValues = PostFormValues | ReplyEditorFormValues;

type DocumentEditorProps = {
  placeholder?: string;
  isEditing?: boolean;
  cancelEdit?: () => void;
  control: Control<AllFormValues>;
  errors: FieldErrors<AllFormValues>;
  setValue: (name: string, value: unknown) => void;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  spaceId?: string;
};

export default function DocumentEditor({
  placeholder,
  isEditing = false,
  cancelEdit,
  control,
  errors,
  setValue,
  onSubmit,
  isSubmitting,
  spaceId,
}: DocumentEditorProps) {
  const { t } = useTranslation();
  const [videoInputOpen, setVideoInputOpen] = useState(false);
  const { trackEvent } = useCommunityContext();
  const [showToolbar, setShowToolbar] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { field: bodyField } = useController({ control, name: 'body' });
  const { isUploading, uploadFiles, generatePreviews } = useFileOperations();
  const bodyFieldValue = useWatch({ control, name: 'body' });
  const attachments = useWatch({ control, name: 'attachments' });
  const { getDraftPost } = useGlobalState();

  useEffect(() => {
    for (const key in errors) {
      if (key === 'body') {
        const message =
          errors[key]?.jsonContent?.message || errors[key]?.plainText?.message;
        setError(message as string);
      } else if (errors[key]?.message) {
        setError(errors[key].message as string);
      }
    }
  }, [errors]);

  useEffect(() => {
    // If we're editing and there's a video attachment, open the video input
    if (
      isEditing &&
      attachments?.some(
        (att) => att.attachmentType === PostAttachmentType.Video
      )
    ) {
      setVideoInputOpen(true);
    }
  }, [isEditing, attachments]);

  useEffect(() => {
    if (!isSubmitting && attachments.length === 0) {
      setVideoInputOpen(false);
    }
  }, [isSubmitting, attachments]);

  const handleBodyChange = (
    value: Content,
    callback: (fieldValue: FieldValue<Body>) => void
  ) => {
    callback({
      jsonContent: value.json,
      plainText: value.text,
    });
  };
  const { editor } = useEditor({
    content: bodyFieldValue?.jsonContent || undefined,
    onBlur: (value: Content) => handleBodyChange(value, bodyField.onChange),
    onChange: (value: Content) => {
      handleBodyChange(value, bodyField.onChange);
      setError(null);
    },
    version: 'editable',
    placeholder,
    className: css.editorContent,
  });

  useEffect(() => {
    if (!isSubmitting && !bodyFieldValue?.jsonContent && editor) {
      editor.commands.clearContent();
    }
  }, [bodyFieldValue?.jsonContent, isSubmitting]);

  const trackFormatting = (type: string) => {
    trackEvent(TrackingEvent.FORMATTING_CLICKED, {
      [TrackingProperty.FORMATTING_TYPE]: type,
    });
  };

  const trackAttachment = (type: string) => {
    trackEvent(TrackingEvent.ATTACHMENT_CLICKED, {
      [TrackingProperty.ATTACHMENT]: type,
    });
  };

  const handleSubmit = () => {
    onSubmit?.();
  };

  const onFileSelected = async (files: File[]) => {
    const currentAttachments = control._formValues.attachments;
    setError(null);
    const validationError = validateFiles(
      files,
      currentAttachments?.length || 0
    );
    if (validationError) {
      setError(validationError);
      return;
    }

    const previews = generatePreviews(files);

    setValue('attachments', [...currentAttachments, ...previews]);

    const filesToUpload = files.map((file, index) => ({
      file,
      tempId: previews[index].tempId,
    }));

    const {
      attachments: uploadedAttachments,
      error: uploadError,
    } = await uploadFiles(filesToUpload);

    if (uploadError) {
      setError(uploadError);
    } else {
      // Replace the previews with the permanent attachments
      const existingAttachments = control._formValues.attachments;
      const updatedAttachments = existingAttachments.map((att) => {
        const found = uploadedAttachments.find((a) => a.tempId === att.tempId);
        if (found) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { tempId, ...rest } = found;
          return rest;
        }
        return att;
      });
      setValue('attachments', updatedAttachments);
    }
  };

  const removeAttachment = (index: number) => {
    setValue(
      'attachments',
      attachments.filter((_, i) => i !== index)
    );
  };

  const onVideoSelected = (attachment: Attachment) => {
    setValue('attachments', [...attachments, attachment]);
  };

  const closeVideoInput = () => {
    // remove video from attachments if it exists
    setValue(
      'attachments',
      attachments.filter(
        (attachment) => attachment.attachmentType !== PostAttachmentType.Video
      )
    );
    setVideoInputOpen(false);
  };

  const updateAltText = (altText: string, index: number) => {
    const currentAttachments = control._formValues.attachments;
    setValue(
      'attachments',
      currentAttachments.map((attachment, i) =>
        i === index ? { ...attachment, altText } : attachment
      )
    );
  };

  useEffect(() => {
    if (isEditing) return;
    const draftPost = spaceId && getDraftPost(spaceId);
    if (draftPost) {
      editor?.commands?.setContent(draftPost.body?.jsonContent);
      setValue('attachments', draftPost.attachments);
      if (draftPost.body) {
        setValue('body', draftPost.body);
      }
    }
  }, [spaceId]);

  return (
    <div className={css.document}>
      {editor && (
        <MenuBar
          editor={editor}
          showToolbar={showToolbar}
          onActionClick={(action) => trackFormatting(action)}
        />
      )}
      <div className={css.content}>
        <EditorContent className={css.editorContent} editor={editor} />
        {error && (
          <InlineNotification
            a11yImportance="assertive"
            actions={[]}
            description={error}
            title="Error"
            variant="danger"
            dismissible
            onDismiss={() => setError(null)}
          />
        )}
        {videoInputOpen && (
          <AttachmentVideoInput
            attachmentUrl={
              attachments.find(
                (att) => att.attachmentType === PostAttachmentType.Video
              )?.url
            }
            onChange={onVideoSelected}
            onClose={closeVideoInput}
          />
        )}
        {attachments.length > 0 && (
          <div className={css.attachmentContainer}>
            {attachments.map((attachment, index) => (
              <AttachmentPreview
                attachment={attachment}
                attachmentType={attachment.attachmentType}
                key={attachment.id}
                t={t}
                onAltTextChange={(altText: string) =>
                  updateAltText(altText, index)
                }
                onRemove={() => removeAttachment(index)}
              />
            ))}
          </div>
        )}
      </div>
      <div className={css.footerToolbar}>
        <div className={css.actions}>
          <ActionButton
            arialLabel={
              t(
                'community.documentEditor.menuBar.showToolbar',
                'Show Toolbar'
              ) as string
            }
            dataQa="post-editor__show-toolbar"
            disabled={false}
            icon={TextAa}
            isActive={showToolbar}
            onClick={() => {
              trackFormatting('textFormatting');
              setShowToolbar(!showToolbar);
            }}
          />
          <EmojiPicker
            editor={editor}
            icon={Smiley}
            t={t}
            onClick={() => {
              trackFormatting('emoji');
            }}
          />
          <span className={css.separator}>|</span>
          {[Image, Paperclip].map((Icon) => (
            <AttachmentInput
              accept={ACCEPTED_FILE_TYPES}
              ariaControls="attachment-file-picker__input"
              icon={Icon}
              key={Icon.displayName}
              label={t(
                'communities.documentEditor.attachToPost',
                `Attach ${Icon.displayName}`,
                {
                  type: Icon.displayName,
                }
              )}
              multiple
              onBtnClicked={() => {
                trackAttachment(
                  Icon.displayName === 'Image' ? 'image' : 'file'
                );
              }}
              onFileSelected={onFileSelected}
            />
          ))}
          <ActionButton
            arialLabel={t(
              'communities.documentEditor.attachToPost',
              'Attach video',
              {
                type: 'video',
              }
            )}
            dataQa="post-editor__video"
            disabled={videoInputOpen}
            icon={VideoCamera}
            isActive={videoInputOpen}
            onClick={() => {
              trackAttachment('video');
              setVideoInputOpen(true);
            }}
          />
        </div>
        <div className={css.submissions}>
          {isEditing && (
            <Button
              size="sm"
              type="button"
              variant="default"
              onClick={cancelEdit}
            >
              {t('Cancel', 'Cancel')}
            </Button>
          )}
          <Button
            dataQa="post-form__submit"
            disabled={isUploading}
            isLoading={isSubmitting}
            size="sm"
            type="submit"
            variant="primary"
            onClick={handleSubmit}
          >
            {isEditing
              ? t('components-richTextEditor-save', 'Save')
              : t('components-richTextEditor-post', 'Post')}
          </Button>
        </div>
      </div>
    </div>
  );
}
